import React, { useState } from 'react';
import { Globe, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { clientLogger } from '../utils/logger';

/**
 * Form component for manually adding domains to the processing queue
 * 
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback when domain is successfully queued
 */
const ManualDomainQueueForm = ({ onSuccess }) => {
    const [domain, setDomain] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!domain.trim()) {
            setError('Please enter a domain');
            return;
        }
        
        // Clear previous results
        setError('');
        setResult(null);
        setIsLoading(true);
        
        try {
            // Normalize the domain (remove protocol, www, trailing slashes)
            let normalizedDomain = domain.trim().toLowerCase();
            
            // If it looks like a URL, extract just the domain
            if (normalizedDomain.includes('://') || normalizedDomain.startsWith('www.')) {
                try {
                    const url = new URL(normalizedDomain.startsWith('http') ? normalizedDomain : `https://${normalizedDomain}`);
                    normalizedDomain = url.hostname.replace(/^www\./, '');
                } catch (urlError) {
                    // If URL parsing fails, just use the input as is
                    normalizedDomain = normalizedDomain.replace(/^www\./, '');
                }
            }
            
            clientLogger.debug('ManualDomainQueueForm: Queueing domain', { domain: normalizedDomain });
            
            // Get user data from localStorage
            const userDataString = localStorage.getItem('userData');
            const userData = userDataString ? JSON.parse(userDataString) : null;
            
            if (!userData || !userData.id) {
                throw new Error('User not logged in');
            }
            
            // Get current organization if available
            const orgDataString = localStorage.getItem('currentOrganization');
            const orgData = orgDataString ? JSON.parse(orgDataString) : null;
            
            // Call the API to queue the domain
            const response = await fetch('/api/domains/queue', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': userData.id
                },
                body: JSON.stringify({
                    domain: normalizedDomain,
                    organizationId: orgData?.id || null,
                    forceQueue: true
                })
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to queue domain');
            }
            
            const data = await response.json();
            clientLogger.debug('ManualDomainQueueForm: Domain queued successfully', data);
            
            // Set success result
            setResult({
                success: true,
                message: data.alreadyQueued 
                    ? 'Domain was already in queue but has been re-queued' 
                    : 'Domain added to processing queue',
                data: {
                    ...data,
                    domain: data.domain || normalizedDomain
                }
            });
            
            // Clear the input
            setDomain('');
            
            // Call success callback if provided
            if (onSuccess) {
                onSuccess(data);
            }
        } catch (error) {
            clientLogger.error('ManualDomainQueueForm: Error queueing domain', error);
            setError(error.message || 'Failed to queue domain');
            setResult({
                success: false,
                message: error.message || 'Failed to queue domain'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Globe className="mr-2 text-green-600" size={20} />
                Queue Domain for Processing
            </h2>
            
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
                        Domain Name
                    </label>
                    <input
                        type="text"
                        id="domain"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Enter a domain name to queue for brand information processing
                    </p>
                </div>
                
                <button
                    type="submit"
                    disabled={isLoading || !domain.trim()}
                    className={`w-full py-2 px-4 rounded-md text-white font-medium flex items-center justify-center
                        ${isLoading || !domain.trim() 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-green-600 hover:bg-green-700'}`}
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="animate-spin mr-2" size={18} />
                            Processing...
                        </>
                    ) : (
                        <>Queue Domain</>
                    )}
                </button>
            </form>
            
            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                    <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}
            
            {result && result.success && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                        <p className="text-sm text-green-700">{result.message}</p>
                        {result.data && (
                            <p className="text-xs text-green-600 mt-1">
                                Job ID: {result.data.jobId}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualDomainQueueForm; 